import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ResourceAdmin({ resource, title, subtitle, fields, sortBy = "order" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const endpoint = `/admin/${resource}`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint + "/");
      setItems(r.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource]);

  const startNew = () => {
    const blank = {};
    fields.forEach((f) => { blank[f.name] = f.default ?? (f.type === "boolean" ? true : f.type === "number" ? 0 : ""); });
    setEditing(blank);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      fields.forEach((f) => {
        if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0);
      });
      if (editing.id) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success("Saved");
      } else {
        await api.post(endpoint + "/", payload);
        toast.success("Created");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid={`admin-${resource}-page`}>
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Manage</div>
          <h1 className="font-serif text-4xl mt-2 text-ink">{title}</h1>
          {subtitle && (
            <p className="mt-2 text-ink/60 text-sm max-w-2xl">{subtitle}</p>
          )}
        </div>
        <button onClick={startNew} data-testid={`${resource}-new-btn`}
                className="inline-flex items-center gap-2 rounded-full bg-emerald_deep text-cream px-5 py-2.5 text-sm font-semibold hover:bg-emerald_deep-900">
          <Plus size={14} /> New
        </button>
      </div>

      {loading ? (
        <div className="text-ink/40">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-white border border-sand p-14 text-center text-ink/60">No {title.toLowerCase()} yet.</div>
      ) : (
        <div className="rounded-3xl bg-white border border-sand admin-scroll admin-scroll-hint">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-sand/50 text-left">
              <tr>
                {fields.filter((f) => f.list).map((f) => (
                  <th key={f.name} className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">{f.label}</th>
                ))}
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-sand hover:bg-cream/60" data-testid={`${resource}-row-${it.id}`}>
                  {fields.filter((f) => f.list).map((f) => (
                    <td key={f.name} className="px-5 py-3 text-ink">
                      {f.type === "boolean" ? (it[f.name] ? "✓" : "—") :
                       f.type === "image" ? (it[f.name] ? <img src={it[f.name]} alt="" className="h-10 w-16 object-cover rounded-md" /> : "—") :
                       String(it[f.name] ?? "").slice(0, 60) || "—"}
                    </td>
                  ))}
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => setEditing(it)} className="p-2 text-ink/60 hover:text-emerald_deep" data-testid={`edit-${it.id}`}><Pencil size={14} /></button>
                    <button onClick={() => del(it.id)} className="p-2 text-ink/60 hover:text-destructive" data-testid={`delete-${it.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white border border-sand p-8 md:p-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl text-ink">{editing.id ? "Edit" : "New"} {title.replace(/s$/, "")}</h2>
              <button onClick={() => setEditing(null)} className="p-2 text-ink/60 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {fields.map((f) => (
                <div key={f.name}>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea
                      rows={f.rows || 4}
                      value={editing[f.name] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                      data-testid={`field-${f.name}`}
                      className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-emerald_deep resize-none"
                    />
                  ) : f.type === "boolean" ? (
                    <label className="mt-2 flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!editing[f.name]}
                        onChange={(e) => setEditing({ ...editing, [f.name]: e.target.checked })}
                        data-testid={`field-${f.name}`}
                      />
                      <span className="text-sm text-ink/70">{f.checkboxLabel || "Enabled"}</span>
                    </label>
                  ) : f.type === "select" ? (
                    <select
                      value={editing[f.name] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                      data-testid={`field-${f.name}`}
                      className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-emerald_deep"
                    >
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      type={f.type === "number" ? "number" : f.type === "datetime" ? "datetime-local" : "text"}
                      value={editing[f.name] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                      data-testid={`field-${f.name}`}
                      className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-emerald_deep"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="rounded-full px-6 py-2.5 text-sm font-medium text-ink/70 hover:bg-sand/50">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="save-btn"
                      className="rounded-full bg-emerald_deep text-cream px-6 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-emerald_deep-900 disabled:opacity-60 inline-flex items-center gap-2">
                {saving && <Loader2 size={14} className="animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
